/**
 * bandwidth-overlay.tsx -- Bandwidth governor overlay for viewer tiles.
 *
 * Renders visual indicators for governor-applied states so users can
 * distinguish between bandwidth-related degradation and actual offline.
 *
 * Per rule: architecture-principles -- works in both Electron and browser.
 * Per rule: code-comments-required -- comments explain intent, not code.
 */
import type { ReactElement } from 'react';
import type { DeviceGovernorState } from '#~/livekit/bandwidth-governor.js';

/** Props for the BandwidthOverlay component. */
export interface BandwidthOverlayProps {
  /** The governor-applied state for this device */
  governorState: DeviceGovernorState;
}

/**
 * Overlay for bandwidth-governed video tiles. Renders differently based on
 * the governor's per-device state to give users clear feedback about why a
 * video stream is degraded or paused.
 */
export const BandwidthOverlay = ({ governorState }: BandwidthOverlayProps): ReactElement | null => {
  // Normal state -- no overlay needed, video plays at full quality
  if (governorState === 'normal') {
    return null;
  }

  if (governorState === 'quality-capped') {
    // Subtle indicator -- video is still playing but at reduced quality
    return (
      <div
        className='gap-1.5 rounded-md bg-black/60 py-1 text-amber-300 pointer-events-none absolute bottom-2 left-2 flex items-center px-2 text-xs'
        aria-label='Low bandwidth mode active'
      >
        <LowBandwidthIcon />
        <span>Low Bandwidth</span>
      </div>
    );
  }

  if (governorState === 'paused-auto') {
    // Full overlay -- video is paused by the governor to save bandwidth
    return renderPausedOverlay('Video paused to save bandwidth', 'Paused (Low Bandwidth)');
  }

  if (governorState === 'paused-manual') {
    // Full overlay -- video is paused by user's manual low-bandwidth toggle
    return renderPausedOverlay('Video paused manually', 'Paused (Manual)');
  }

  return null;
};

/** Shared paused-state markup keeps automatic and manual overlays visually identical. */
const renderPausedOverlay = (ariaLabel: string, caption: string): ReactElement => (
  <div
    className='bg-dark/80 absolute inset-0 grid content-center justify-center rounded-12 text-base-100'
    aria-label={ariaLabel}
  >
    <div className='flex flex-col items-center gap-2'>
      <PausedIcon />
      <span className='text-neutral-300 text-sm'>{caption}</span>
    </div>
  </div>
);

/** Small bandwidth icon for the quality-capped indicator */
const LowBandwidthIcon = (): ReactElement => (
  <svg className='h-3.5 w-3.5 fill-current' viewBox='0 0 16 16' aria-hidden='true'>
    <path d='M1 12h2v3H1zm4-3h2v6H5zm4-3h2v9H9zm4-4h2v13h-2z' opacity='0.3' />
    <path d='M1 12h2v3H1zm4-3h2v6H5z' />
  </svg>
);

/** Pause icon for the paused state overlays */
const PausedIcon = (): ReactElement => (
  <svg className='text-neutral-400 h-10 w-10 fill-current' viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M6 4h4v16H6zm8 0h4v16h-4z' />
  </svg>
);
